import {
  Form,
  FormSubmitButton,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUpdateMassTort } from "@/lib/data/react-query/mass-torts/mass-tort";
import { type MassTort } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const formSchema = z.object({
  massTortTitle: z
    .string()
    .trim()
    .min(1, { message: "Title is required" })
    .max(500, { message: "Title must be 500 characters or less" }),
  massTortDescription: z
    .string()
    .max(5000, { message: "Description must be 5,000 characters or less" }),
});

type UpdateMassTortFormValues = z.infer<typeof formSchema>;

const UPDATE_MASS_TORT_FORM_ID = "update-mass-tort-form";

interface UpdateMassTortModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  massTort: MassTort;
  massTortId: string;
}

export function UpdateMassTortModal({
  open,
  setOpen,
  massTort,
  massTortId,
}: UpdateMassTortModalProps) {
  const initialValues = useMemo<UpdateMassTortFormValues>(
    () => ({
      massTortTitle: massTort.title?.trim() ?? "",
      massTortDescription: massTort.description?.trim() ?? "",
    }),
    [massTort.description, massTort.title],
  );

  const form = useForm<UpdateMassTortFormValues>({
    resolver: zodResolver(formSchema),
    values: initialValues,
    mode: "onSubmit",
  });

  const updateMassTort = useUpdateMassTort();
  const isFormLoading = updateMassTort.isPending;

  useEffect(() => {
    if (!open) {
      form.reset(initialValues);
    }
  }, [form, initialValues, open]);

  const onSubmit = (data: UpdateMassTortFormValues) => {
    if (!massTort.dateModified) {
      return;
    }

    const description = data.massTortDescription.trim();
    updateMassTort.mutate(
      {
        id: massTortId,
        dateModified: massTort.dateModified,
        title: data.massTortTitle.trim(),
        description: description ? description : null,
      },
      {
        onSuccess: () => {
          setOpen(false);
          form.reset({
            massTortTitle: data.massTortTitle.trim(),
            massTortDescription: description,
          });
        },
      },
    );
  };

  return (
    <Form {...form}>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        title="Edit mass tort"
        description="Update the title and description for this mass tort."
        size="xl"
        isDirty={form.formState.isDirty}
        loading={isFormLoading}
        footer={
          <div className="flex w-full flex-wrap justify-end gap-2">
            <FormCancelButton
              isDirty={form.formState.isDirty}
              setOpen={setOpen}
            />
            <FormSubmitButton
              title="Save changes"
              loading={isFormLoading}
              submitFormId={UPDATE_MASS_TORT_FORM_ID}
            />
          </div>
        }
      >
        <form
          id={UPDATE_MASS_TORT_FORM_ID}
          className="relative"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit(onSubmit)();
          }}
        >
          <fieldset className={cn(slideoverFieldsetClassName, "my-0 p-0")}>
            <TextFormField
              register={form.register("massTortTitle")}
              label="Title*"
            />
            <div className="col-span-2">
              <TextAreaFormField
                register={form.register("massTortDescription")}
                label="Description"
              />
            </div>
          </fieldset>
        </form>
      </AppModal>
    </Form>
  );
}
